import { html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import BaseBannerMultiple from './base-banner-multiple';
import { trackPrintError, trackCall } from './utils/tracking';
import stylesHMBDouble from './styles/horizontal-medium-banner-double';
import { resizeObserver } from './utils/resize-observer';

const defaultValues = {
  items: [],
  gapGrid: '',
  justifyContent: 'center',
  preloadImages: true,
  lazyloadImages: 'off',
  is_preview: false,
};

@customElement('horizontal-medium-banner-double')
export default class HorizontalMediumBannerDouble extends BaseBannerMultiple {
  @property({ type: Array }) items: HmbDoubleProps[] = defaultValues.items;

  @property({ type: String }) gapGrid = defaultValues.gapGrid;

  @property({ type: String }) justifyContent = defaultValues.justifyContent;

  @property({ type: Boolean }) preloadImages = defaultValues.preloadImages;

  @property({ type: String }) lazyloadImages = defaultValues.lazyloadImages;

  @property({ type: Boolean }) is_preview = defaultValues.is_preview;

  static styles = [stylesHMBDouble];

  private resizeObserverInstance: ResizeObserver | null = null;

  protected firstUpdated(): void {
    const container = this.shadowRoot?.querySelector('.hmb-double-container') as HTMLElement;
    this.style.setProperty('--container-width', `${container?.offsetWidth || '0'}px`);

    const resizeObserverInstance = resizeObserver(this.style);
    resizeObserverInstance.observe(container);
  }

  trackPrintUrl(url: string) {
    try {
      const trackObject: TrackUrl = { url };
      trackCall(trackObject);
    } catch (error) {
      trackPrintError('Error al ejecutar trackPrintUrl');
    }
  }

  renderContent(item: HmbDoubleProps): TemplateResult {
    const {
      content: {
        images: { picture, primary_logo, secondary_logo },
        texts: { cta, primary_title, secondary_title, volanta, legal },
        colors: { card_background_color, card_text_color },
      },
      event_tracking: {
        print: [{ url: printUrl = '' } = {}],
        view: [{ url: viewUrl = '' } = {}],
      },
    } = item;

    if (printUrl) {
      this.trackPrintUrl(printUrl);
    }

    if (viewUrl) {
      this.view_url = JSON.stringify([{ url: viewUrl }]);
      this.sendViewTrack();
    }

    return html` <div
      class="hmb-double-container"
      style=${styleMap({
    background: card_background_color,
  })}
    >
      <div
        class="hmb-double-left-side"
        style=${styleMap({
    color: card_text_color,
  })}
      >
        ${picture ? this.renderImage('hmb-double-picture', picture) : ''}
      </div>
      <div class="hmb-double-right-side">${this.renderMultipleLogos(primary_logo, secondary_logo, 'hmb-double')}
        ${this.renderMultipleContentTexts(primary_title, secondary_title, volanta, cta, legal, card_text_color, card_background_color, 'hmb-double', true)}
      </div>
    </div>`;
  }

  renderClickTemplate(item: HmbDoubleProps): TemplateResult {
    const {
      content: {
        a11y: { context_banner = '' } = {},
      },
      event_tracking: {
        click: [{ url: clickUrl = '' } = {}],
      },
    } = item;

    return html`
      <a class="hmb-double-content-target" href="${clickUrl}" aria-label=${context_banner}>${this.renderContent(item)}</a>`;
  }

  render() {
    if (!this.items || this.items.length !== 2) {
      return null;
    }
    const style = {
      ...(this.gapGrid ? { gap: this.gapGrid } : {}),
      justifyContent: this.justifyContent,
    };
    return html`
      <div data-testid="hmb-double" class="hmb-double-wrapper" style=${styleMap(style)}>
        ${this.items.map(
    (item: HmbDoubleProps): TemplateResult => (this.is_preview ? this.renderContent(item) : this.renderClickTemplate(item)),
  )}
      </div>
    `;
  }
}
